import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LIMITS } from '@kidzim/shared';
import { Badge, Button, ChatBubble, Logo } from '../design-system/components';
import { useAuth } from '../lib/auth';
import { useChat } from '../lib/useChat';
import { isChatMuted, playDing, setChatMuted } from '../lib/sound';

export function ChatRoom() {
  const { roomId = '' } = useParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { status, messages, opensAt, reason, notice, remainingSeconds, title, kind, typingNames, send, setTyping } = useChat(roomId, true);
  const [draft, setDraft] = useState('');
  const [muted, setMuted] = useState(isChatMuted());
  const endRef = useRef<HTMLDivElement | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the newest message we've already seen, so we chime only for genuinely
  // new incoming messages — never for the history backlog or our own messages.
  const lastSeenId = useRef<string | null>(null);
  const seeded = useRef(false);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, typingNames.length]);

  // Ding on a new message from someone else.
  useEffect(() => {
    const latest = messages[messages.length - 1];
    if (!latest) return;
    if (!seeded.current) {
      // First populated render = the history backlog; remember it, don't chime.
      seeded.current = true;
      lastSeenId.current = latest.id;
      return;
    }
    if (latest.id !== lastSeenId.current) {
      lastSeenId.current = latest.id;
      if (latest.authorId !== user?.id) playDing();
    }
  }, [messages, user?.id]);

  function toggleMute() {
    setMuted((m) => {
      setChatMuted(!m);
      return !m;
    });
  }

  async function signOut() {
    await logout();
    nav('/');
  }

  // Tell the room we're typing, then auto-clear shortly after the kid stops.
  function onDraftChange(value: string) {
    setDraft(value);
    setTyping(true);
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => setTyping(false), 2500);
  }

  useEffect(() => () => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
  }, []);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    send(body);
    setDraft('');
    if (typingTimer.current) clearTimeout(typingTimer.current);
    setTyping(false);
  }

  if (status === 'waiting') return <WaitingRoom opensAt={opensAt} reason={reason} />;
  if (status === 'closed') return <ClosedRoom reason={reason} kind={kind} />;

  return (
    <div className="kz-app-shell kz-chat-shell">
      <header className="kz-row" style={{ padding: 'var(--space-4)', gap: 'var(--space-2)', rowGap: 'var(--space-2)', flexWrap: 'wrap', borderBottom: 'var(--border-width) solid var(--border-default)', background: 'var(--white)' }}>
        <Logo height={28} />
        {title && <span style={{ font: 'var(--type-h3)', color: 'var(--ink-800)' }}>{title}</span>}
        <span className="kz-grow" />
        {remainingSeconds !== null && remainingSeconds < 600 && (
          <Badge tone="warn">⏳ {formatMinutesLeft(remainingSeconds)} left today</Badge>
        )}
        <Badge tone="safe">✓ A grown-up is keeping this safe</Badge>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'Turn message sounds on' : 'Turn message sounds off'}
          title={muted ? 'Sound off' : 'Sound on'}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 'var(--text-lg)', lineHeight: 1, padding: 4 }}
        >
          {muted ? '🔕' : '🔔'}
        </button>
        {user?.role === 'kid' && (
          <Button variant="ghost" size="sm" onClick={signOut}>
            Sign out
          </Button>
        )}
      </header>

      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <ChatBubble variant="system">Welcome to the chat — be kind and have fun! 🎈</ChatBubble>
        {messages.map((m) => (
          <ChatBubble
            key={m.id}
            mine={m.authorId === user?.id}
            author={m.authorName}
            time={new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          >
            {m.authorKind === 'grownup' && m.authorId !== user?.id && (
              <span style={{ display: 'inline-block', marginRight: 6, verticalAlign: 'middle' }}>
                <Badge tone="safe">👋 Grown-up</Badge>
              </span>
            )}
            {m.body}
            {m.moderation !== 'clean' && (
              <span style={{ display: 'block', marginTop: 4, font: 'var(--type-mono)', fontSize: 'var(--text-2xs)', opacity: 0.8 }}>
                {moderationNote(m.moderation)}
              </span>
            )}
          </ChatBubble>
        ))}
        <div ref={endRef} />
      </div>

      <div style={{ minHeight: 20, padding: '0 16px' }} aria-live="polite">
        {typingNames.length > 0 && (
          <span style={{ font: 'var(--type-small)', color: 'var(--ink-500)' }}>{typingLabel(typingNames)}</span>
        )}
      </div>

      {notice && (
        <div style={{ padding: '6px 16px', textAlign: 'center', color: 'var(--ink-500)', font: 'var(--type-small)' }}>{notice}</div>
      )}

      <form onSubmit={submit} className="kz-row" style={{ padding: 'var(--space-4)', gap: 'var(--space-2)', borderTop: 'var(--border-width) solid var(--border-default)', background: 'var(--white)' }}>
        <input
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          maxLength={LIMITS.MAX_MESSAGE_LEN}
          placeholder="Say something nice…"
          className="kz-grow"
          name="kidzim-message"
          autoComplete="off"
          autoCorrect="off"
          spellCheck
          enterKeyHint="send"
          data-1p-ignore
          data-lpignore="true"
          style={{ font: 'var(--type-body)', border: 'var(--border-width) solid var(--border-default)', borderRadius: 'var(--radius-pill)', padding: '11px 16px' }}
        />
        <Button type="submit" disabled={!draft.trim()}>
          Send
        </Button>
      </form>
    </div>
  );
}

function typingLabel(names: string[]): string {
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return 'Several friends are typing…';
}

function moderationNote(m: string): string {
  if (m === 'link_blocked') return 'A link was removed to keep things safe.';
  if (m === 'image_blocked') return 'A picture was removed to keep things safe.';
  if (m === 'flagged') return 'Some details were hidden for safety.';
  return '';
}

/** Title/body copy for a waiting/closed screen, keyed by the server's `reason`. */
function reasonCopy(reason: string | null, fallback: { title: string; body: string }): { title: string; body: string } {
  if (reason === 'budget_exhausted') {
    return { title: "Chat time is done for today 👋", body: "You've used up today's buddy-chat time. It's back tomorrow!" };
  }
  if (reason === 'not_chat_hours') {
    return { title: 'Not chat hours right now 🕒', body: "Buddy chat is only open at certain times. We'll let you in when it's time." };
  }
  return fallback;
}

function WaitingRoom({ opensAt, reason }: { opensAt: string | null; reason: string | null }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const remaining = opensAt ? Math.max(0, new Date(opensAt).getTime() - now) : null;
  const { title, body } = reasonCopy(reason, {
    title: 'Almost time! ⏳',
    body: "The chat opens soon. Hang tight — we'll let you in the moment it starts.",
  });

  return (
    <div className="kz-center">
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <Logo height={44} />
        <h3 style={{ margin: 'var(--space-5) 0 var(--space-2)' }}>{title}</h3>
        <p className="kz-muted">{body}</p>
        {remaining !== null && (
          <p className="kz-mono" style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-2xl)', color: 'var(--blue-600)' }}>
            {formatCountdown(remaining)}
          </p>
        )}
      </div>
    </div>
  );
}

function ClosedRoom({ reason, kind }: { reason: string | null; kind: 'standard' | 'dm' | null }) {
  const { title, body } = reasonCopy(
    reason,
    kind === 'dm'
      ? { title: 'This chat is paused 👋', body: 'A grown-up paused this buddy chat. Say hi again from your buddy list anytime.' }
      : { title: "That's a wrap! 👋", body: 'This chat has closed. Ask a grown-up when the next one opens.' },
  );
  return (
    <div className="kz-center">
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <Logo height={44} />
        <h3 style={{ margin: 'var(--space-5) 0 var(--space-2)' }}>{title}</h3>
        <p className="kz-muted">{body}</p>
      </div>
    </div>
  );
}

function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatMinutesLeft(seconds: number): string {
  return `${Math.max(1, Math.ceil(seconds / 60))} min`;
}
